import React, { useEffect, useMemo, useState } from "react";
import { api, STATUSES, statusColor, PRIORITY_ORDER, priorityColor, priorityLabel, locationSuffix } from "../lib/api";
import { Link } from "react-router-dom";
import { Plus, ArrowUpRight, Target } from "lucide-react";
import { Button } from "../components/ui/button";
import { useSettings } from "../context/SettingsContext";

const ACTIVE = ["saved", "applied", "interviewing"];

export default function Dashboard() {
  const { settings } = useSettings();
  const [stats, setStats] = useState({ saved: 0, applied: 0, interviewing: 0, offer: 0, rejected: 0, total: 0 });
  const [allJobs, setAllJobs] = useState([]);
  const [focus, setFocus] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, j] = await Promise.all([api.get("/stats"), api.get("/jobs")]);
        setStats(s.data);
        setAllJobs(j.data);
        const active = j.data.filter((x) => ACTIVE.includes(x.status));
        active.sort((a, b) => {
          const pa = PRIORITY_ORDER[a.priority] ?? 2;
          const pb = PRIORITY_ORDER[b.priority] ?? 2;
          if (pa !== pb) return pa - pb;                      // highest priority first
          return (a.updated_at || "").localeCompare(b.updated_at || ""); // then stalest first
        });
        setFocus(active.slice(0, 5));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const showRejected = settings.show_rejected_in_main;
  const visibleStatuses = showRejected ? STATUSES : STATUSES.filter((s) => s.key !== "rejected");
  const recent = useMemo(() => {
    const arr = showRejected ? allJobs : allJobs.filter((x) => x.status !== "rejected");
    return arr.slice(0, 6);
  }, [allJobs, showRejected]);

  return (
    <div className="space-y-10" data-testid="dashboard-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tiny">Overview</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Pipeline</h1>
        </div>
        <Link to="/jobs/new">
          <Button data-testid="dashboard-add-job-btn" className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-10 px-4">
            <Plus size={16} className="mr-1" /> New job
          </Button>
        </Link>
      </div>

      {/* Today's focus */}
      {!loading && focus.length > 0 && (
        <section className="border-2 border-zinc-900" data-testid="focus-widget">
          <div className="flex items-center justify-between px-5 py-3 bg-zinc-900 text-white">
            <div className="label-tiny flex items-center gap-2 text-white">
              <Target size={13} /> Today's focus
            </div>
            <span className="font-mono-jb text-xs text-zinc-400">Top {focus.length} · by priority</span>
          </div>
          <div>
            {focus.map((j) => (
              <Link
                key={j.job_id}
                to={`/jobs/${j.job_id}`}
                data-testid={`focus-job-${j.job_id}`}
                className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="status-dot shrink-0" style={{ backgroundColor: priorityColor(j.priority) }} title={`${priorityLabel(j.priority)} priority`} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{j.title}</div>
                    <div className="text-sm text-zinc-500 truncate">{j.company}{locationSuffix(j)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono-jb text-[10px] uppercase tracking-wider text-zinc-500 hidden sm:inline">{priorityLabel(j.priority)}</span>
                  <span className="flex items-center text-xs">
                    <span className="status-dot" style={{ backgroundColor: statusColor(j.status) }} />
                    <span className="font-mono-jb uppercase tracking-wider hidden sm:inline">{j.status}</span>
                  </span>
                  <ArrowUpRight size={16} className="text-zinc-400" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Bento grid */}
      <div className="grid grid-cols-2 lg:grid-cols-6 border border-zinc-200">
        <div className="p-5 lg:col-span-2 border-b lg:border-b-0 lg:border-r border-zinc-200" data-testid="stat-total">
          <div className="label-tiny">Total</div>
          <div className="font-mono-jb text-5xl sm:text-6xl tracking-tight mt-3">{loading ? "—" : stats.total}</div>
        </div>
        {visibleStatuses.map((s, i) => (
          <div
            key={s.key}
            className={"p-5 border-zinc-200 " + (i < visibleStatuses.length - 1 ? "border-r " : "") + (i < 2 && "lg:border-r")}
            data-testid={`stat-${s.key}`}
          >
            <div className="flex items-center">
              <span className="status-dot" style={{ backgroundColor: s.color }} />
              <span className="label-tiny">{s.label}</span>
            </div>
            <div className="font-mono-jb text-3xl sm:text-4xl mt-3">{loading ? "—" : stats[s.key]}</div>
          </div>
        ))}
      </div>

      {/* Recent */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">Recent activity</h2>
          <Link to="/jobs" className="label-tiny hover:text-zinc-900" data-testid="dashboard-see-all">See all →</Link>
        </div>

        {loading ? (
          <div className="space-y-2">
            {["a", "b", "c", "d"].map((k) => <div key={k} className="h-16 bg-zinc-100" />)}
          </div>
        ) : recent.length === 0 ? (
          <div className="brutal-card p-10 text-center">
            <div className="label-tiny">Nothing here yet</div>
            <p className="mt-3 text-zinc-600">Add your first job to get started.</p>
            <Link to="/jobs/new">
              <Button data-testid="empty-add-job-btn" className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white mt-6 h-10">
                <Plus size={16} className="mr-1" /> Add a job
              </Button>
            </Link>
          </div>
        ) : (
          <div className="border border-zinc-200">
            {recent.map((j) => (
              <Link
                key={j.job_id}
                to={`/jobs/${j.job_id}`}
                data-testid={`recent-job-${j.job_id}`}
                className="flex items-center justify-between p-4 border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{j.title}</div>
                  <div className="text-sm text-zinc-500 truncate">{j.company}{j.location ? ` · ${j.location}` : ""}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="hidden sm:flex items-center text-xs">
                    <span className="status-dot" style={{ backgroundColor: statusColor(j.status) }} />
                    <span className="font-mono-jb uppercase tracking-wider">{j.status}</span>
                  </span>
                  <ArrowUpRight size={16} className="text-zinc-400" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
